/*
 * sidebar-collapse-menu-button
 */
import { DropdownMenuArrow } from "@radix-ui/react-dropdown-menu"
import { Link, useLocation } from "@tanstack/react-router"
import { cn } from "@emr/react-ui"
import {
	Button,
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@emr/react-ui"
import { ArrowRight, ChevronDown } from "lucide-react"
import { type ReactNode, useState } from "react"
import { useIsInSheetSidebar } from "./sidebar.context"

type Submenu = {
	to: string
	label: string
	disabled?: boolean
}

interface CollapseMenuButtonProps {
	pathname: string
	Icon: ReactNode
	label: string
	active: boolean
	submenus: Submenu[]
	isOpen: boolean | undefined
	// setIsOpen?: () => void
}

export function CollapseMenuButton({
	pathname,
	Icon,
	label,
	active,
	submenus,
	isOpen,
	// setIsOpen,
}: CollapseMenuButtonProps) {
	const isInSheet = useIsInSheetSidebar()
	const location = useLocation()
	const currentHref = location.pathname + location.searchStr

	// For submenus with query strings (?status=X), match against full href.
	// For plain path submenus, match against pathname only.
	function isSubmenuMatch(to: string): boolean {
		if (to.includes("?")) {
			return currentHref === to || currentHref.startsWith(to + "&")
		}
		return pathname.includes(to)
	}

	const isSubmenuActive = submenus.some((submenu) => isSubmenuMatch(submenu.to))
	const [isCollapsed, setIsCollapsed] = useState<boolean>(isSubmenuActive)

	function handleOnClick(_e: any) {
		// Sheet context handles its own closing behavior
		// Desktop sidebar doesn't need special handling here
	}

	// In Sheet context, always show expanded view; otherwise use isOpen prop
	const shouldShowExpanded = isInSheet || isOpen

	return shouldShowExpanded ? (
		<Collapsible
			open={isCollapsed}
			onOpenChange={setIsCollapsed}
			className={cn(["w-full", isCollapsed ? "bg-card" : ""])}
		>
			<CollapsibleTrigger
				className={"mb-1 [&[data-state=open]>div>div>svg]:rotate-180"}
				asChild
			>
				<Button
					variant={active ? "secondary" : "ghost"}
					className={cn([
						"h-10 w-full justify-start rounded-none pl-6",
						active ? "text-secondary-foreground" : "text-paper-foreground",
						isCollapsed ? "bg-border" : "",
					])}
				>
					<div className={"flex w-full items-center justify-between"}>
						<div className={"flex items-center"}>
							{/* Option - Dropdown */ ""}
							<span className={"ml-[3px] mr-4"}>{Icon}</span>
							<p
								className={cn(
									"truncate",
									isOpen
										? "translate-x-0 opacity-100"
										: "-translate-x-96 opacity-0",
								)}
							>
								{label}
							</p>
							{/* Option - Dropdown */ ""}
						</div>
						<div
							className={cn(
								"whitespace-nowrap",
								isOpen
									? "translate-x-0 opacity-100"
									: "-translate-x-96 opacity-0",
							)}
						>
							<ChevronDown
								className={"size-4 transition-transform duration-200"}
							/>
						</div>
					</div>
				</Button>
			</CollapsibleTrigger>
			<CollapsibleContent
				className={
					"data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden"
				}
			>
				{submenus.map(({ to, label, disabled = false }, index) => (
					<Button
						key={index}
						variant={isSubmenuMatch(to) ? "outline" : "ghost"}
						disabled={disabled}
						className={cn(
							"text-paper-foreground mb-1 h-10 w-full justify-start rounded-none border-none pl-6",
							disabled ? "cursor-not-allowed opacity-50" : "",
						)}
						onClick={handleOnClick}
						asChild
					>
						<Link to={to as any} disabled={disabled}>
							{/* Option - Submenu Link */ ""}
							<span className={"ml-2 mr-4"}>
								<ArrowRight className={"size-4"} />
							</span>
							<p
								className={cn(
									"truncate",
									isOpen
										? "translate-x-0 opacity-100"
										: "-translate-x-96 opacity-0",
								)}
							>
								{label}
							</p>
							{/* Option - Submenu Link */ ""}
						</Link>
					</Button>
				))}
			</CollapsibleContent>
		</Collapsible>
	) : (
		<DropdownMenu>
			<TooltipProvider disableHoverableContent>
				<Tooltip delayDuration={100}>
					<TooltipTrigger asChild>
						<DropdownMenuTrigger asChild>
							<Button
								variant={active ? "secondary" : "ghost"}
								className={
									"text-paper-foreground mb-1 h-10 w-full justify-start rounded-none pl-6"
								}
							>
								<div className={"flex w-full items-center justify-between"}>
									<div className={"flex items-center"}>
										{/* Option - Collapsed */ ""}
										<span
											className={cn(isOpen === false ? "ml-[3px]" : "mr-4")}
										>
											{Icon}
										</span>
										<p
											className={cn(
												"truncate",
												isOpen === false ? "opacity-0" : "opacity-100",
											)}
										>
											{label}
										</p>
										{/* Option - Collapsed */ ""}
									</div>
								</div>
							</Button>
						</DropdownMenuTrigger>
					</TooltipTrigger>
					<TooltipContent side={"right"} align={"start"} alignOffset={2}>
						{label}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
			<DropdownMenuContent side={"right"} sideOffset={25} align={"start"}>
				<DropdownMenuLabel className={"truncate"}>{label}</DropdownMenuLabel>
				<DropdownMenuSeparator />
				{submenus.map(({ to, label, disabled = false }, index) => (
					<DropdownMenuItem key={index} asChild>
						<Link
							className={cn(
								disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
							)}
							to={to as any}
							disabled={disabled}
						>
							<p className={"truncate"}>{label}</p>
						</Link>
					</DropdownMenuItem>
				))}
				<DropdownMenuArrow className={"fill-border"} />
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
